import { SLIDE_ADAPTER } from '@src/framework/workbench/editor-adpater/slide-adapter';
import { IChartElementShower } from '@tencent/chart-drawer-components/lib/interface';
import { Feature } from '@tencent/workbench';
import some from 'lodash/some';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import isEqual from 'lodash/isEqual';
import ChartBaseDrawerFeature from '@src/framework/workbench/drawer/ui/chart/chart-base-drawer-feature';
import { STTrendlineType, SubChartType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import ChartFastLayout from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-fast-layout';
import type { IChartUIFastLayout } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-fast-layout/fast-layout-config';
import { IGridLineProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/grid-line.interface';
import { isTrendlineAvailable } from '@src/slide/domain/ui-model/element-modeles/chart-models/utils/trendline';

@Feature({
    name: 'ChartElementFeature',
    adapters: [SLIDE_ADAPTER],
    provide: IChartElementShower,
})
export class ChartElementShower extends ChartBaseDrawerFeature implements IChartElementShower {
    private chartLayout: ChartFastLayout;
    private layouts: IChartUIFastLayout[] = [];

    public shouldShowTitle() {
        const model = this.getSelectModel();
        const hasChartTitle = !!model.chartProperty?.chartTitle;
        return hasChartTitle;
    }

    public shouldShowLegend() {
        const model = this.getSelectModel();
        const legendProperty = model.chartProperty?.legend;
        const hasLegend = !!legendProperty;
        return hasLegend;
    }

    public shouldShowDataLabel() {
        const model = this.getSelectModel();
        const subChartProperty = model.chartProperty.subCharts;
        const allSeriesProperty = subChartProperty.map(chart => chart.series).flat();
        const hasDataLabels = allSeriesProperty.some(series => series.dataLabels && !series.dataLabels.delete);
        return hasDataLabels;
    }

    public shouldShowAxis() {
        const model = this.getSelectModel();
        const axesProperty = model.chartProperty?.axes;
        const hasAxes = some(axesProperty?.getAllAxes(), axis => axis && !axis.isDelete);
        return !!hasAxes;
    }

    public shouldShowAxisTitle() {
        const model = this.getSelectModel();
        const axesProperty = model.chartProperty?.axes;
        const hasAxisTitle = some(axesProperty?.axisList, axis => !!axis?.title);
        return hasAxisTitle;
    }

    public shouldShowGrid() {
        const model = this.getSelectModel();
        const { charts } = model.plotArea;
        const chartType = charts[0]?.type;
        const axesProperty = model.chartProperty?.axes;
        const axes = axesProperty?.getAllAxes();
        const [, primaryVerticalAxis] = axes || [];
        const hasGrid = chartType === SubChartType.RadarChart
            ? primaryVerticalAxis
                && (this.hasGridline(primaryVerticalAxis.majorGridlines)
                || this.hasGridline(primaryVerticalAxis.minorGridlines))
            : some(
                axes,
                axis => this.hasGridline(axis?.majorGridlines) || this.hasGridline(axis?.minorGridlines),
            );
        return !!hasGrid;
    }

    public shouldShowTrendline() {
        const model = this.getSelectModel();
        if (!model?.plotArea) return false;
        const { charts } = model.plotArea;
        const allSeries = charts.map(chart => chart.series).flat();
        const hasTrenndline = allSeries.some(series => series.trendlines?.some?.(trendline => (
            trendline?.type === STTrendlineType.linear || trendline?.type === STTrendlineType.exp
        )));
        return hasTrenndline;
    }

    public getQuickLayouts() {
        const model = this.getSelectModel();
        if (model?.type === UIModelType.CHART) {
            if (!this.chartLayout || this.chartLayout.getCurChartModel() !== model
                || !isEqual(this.chartLayout.getType(), ChartFastLayout.getChartType(model))) {
                this.chartLayout = new ChartFastLayout(model);
                const { type, subType } = this.chartLayout.getType();
                this.layouts  = ChartFastLayout.getChartLayoutConfigs(type, subType);
            }
        }
        return this.layouts;
    }

    public shouldHideTrendlineSetting() {
        const model = this.getSelectModel();
        return !isTrendlineAvailable(model);
    }

    private hasGridline(gridline?: IGridLineProperty) {
        return gridline && !gridline.props?.style.border?.noFill;
    }
}
